import { service } from '@ember/service'
import Component from '@glimmer/component'
import { isEmpty, isPresent } from '@ember/utils'
import { format, type NormalizedInterval } from 'date-fns'
import { colors } from '@colors'
import {
  reportingPlotLine as plotLine,
  reportingLegend as legend,
} from 'teacher-ui-client/utils/giraffe/highcharts-config'
import type { IntlService } from 'ember-intl'
import type { LegendOptions, Point, SeriesOptionsType, YAxisPlotLinesOptions } from 'highcharts'
import { LeftSplitChart } from 'teacher-ui-client/components/giraffe/left-split-chart'
import { SummaryBox, type Stat } from 'teacher-ui-client/components/giraffe/summary-box'
import { ScatterPlot } from 'teacher-ui-client/components/giraffe/primitives/scatter-plot'
import { SummaryBoxColorScheme } from 'teacher-ui-client/components/giraffe/summary-box/themes'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type LessonInfoService from 'teacher-ui-client/services/lesson-info'
import { Precinct } from 'teacher-ui-client/utils/precincts'

export interface CourseProgressSpellingStudentData {
  summary: {
    total_count: number
    highest_count: number
  }
  events: {
    category: string
    lesson_number: number
    score: number
    created_at: number
  }[]
}

export interface CourseProgressLessonsStudentData {
  summary: {
    total_count: number
  }
  events: {
    category: string
    lesson_number: number
    created_at: number
  }[]
}

interface Args {
  data: CourseProgressSpellingStudentData | CourseProgressLessonsStudentData
  precinct:
    | typeof Precinct.RE_READING
    | typeof Precinct.RE_SPELLING
    | typeof Precinct.REX_READING
    | typeof Precinct.REX_SPELLING
    | typeof Precinct.MS_LESSONS
  summaryTitle: string
  period: NormalizedInterval
  link?: { route: string; text: string }
}

interface Signature {
  Element: HTMLDivElement
  Args: Args
}

export class CourseProgressStudent extends Component<Signature> {
  @service intl!: IntlService
  @service gradeSets!: GradeSetsService
  @service lessonInfo!: LessonInfoService

  readonly chartMargin = [null, 110, null, null]

  readonly legend: LegendOptions = legend('left', 'top', 40, 0)

  readonly yAxisLabel = 'Lesson number'

  get assignmentData() {
    return this.completedAssignmentEvents.map((event) => {
      const timestampInMilliseconds = event.created_at * 1000
      return { x: timestampInMilliseconds, y: event.lesson_number, event }
    })
  }

  get color() {
    switch (this.args.precinct) {
      case Precinct.MS_LESSONS:
        return colors.forestGreen300
      case Precinct.RE_SPELLING:
      case Precinct.REX_SPELLING:
        return colors.grapeyGreen300
      case Precinct.RE_READING:
      case Precinct.REX_READING:
        return colors.oceanyBlue300
      default:
        return colors.oceanyBlue300
    }
  }

  get chartData(): SeriesOptionsType[] {
    const { lessonData, assignmentData, intl } = this

    const lessonColor = this.color

    let lessonTypeLabel = ''
    switch (this.args.precinct) {
      case Precinct.RE_READING:
        lessonTypeLabel = intl.t('reading')
        break
      case Precinct.REX_READING:
      case Precinct.MS_LESSONS:
        lessonTypeLabel = intl.t('lessons')
        break
      case Precinct.RE_SPELLING:
      case Precinct.REX_SPELLING:
        lessonTypeLabel = intl.t('spelling')
        break
    }

    const assignmentColor = colors.purplyPink300
    return [
      {
        name: lessonTypeLabel,
        data: lessonData,
        color: lessonColor,
        type: 'scatter',
      },
      {
        name: 'Assignments',
        data: assignmentData,
        color: assignmentColor,
        type: 'scatter',
      },
    ]
  }

  get completedAssignmentEvents() {
    return this.args.data.events?.filter((event) => event.category === 'assignments') ?? []
  }

  get completedLessonEvents() {
    return this.args.data.events?.filter((event) => event.category === 'lessons') ?? []
  }

  /**
   * The total lessons completed for this student, shown in the summary box
   */
  get lessonsCompleted(): number {
    return this.args.data.summary?.total_count ?? 0
  }

  get lessonData() {
    return this.completedLessonEvents.map((event) => {
      const timestampInMilliseconds = event.created_at * 1000
      return { x: timestampInMilliseconds, y: event.lesson_number, event }
    })
  }

  get lessons() {
    return this.lessonInfo.lessonsFor(this.args.precinct)
  }

  /**
   * Provide the grades for the lessons in the reporting data
   */
  get grades() {
    const lessons = this.args.data.events?.map((event) => event.lesson_number) ?? []
    if (isEmpty(lessons)) return []

    const grades = lessons
      .map((lesson) => this.lessons.find((l) => l.lesson_position === lesson)?.grade_position)
      .filter(isPresent)
    return [...new Set(grades)].sort((a, b) => a - b)
  }

  get plotLines(): YAxisPlotLinesOptions[] {
    // Add the _next_ grade to the list, if it exists in the lesson data. This is so we show the next plotLine too.
    const nextGrade = this.grades[this.grades.length - 1] + 1
    const gradesWithNext = this.lessons.find((l) => l.grade_position === nextGrade)
      ? [...this.grades, nextGrade]
      : this.grades

    return gradesWithNext
      .map((grade) => {
        const firstLessonOfGrade = this.lessons.find((l) => l.grade_position === grade)?.lesson_position
        const label = this.gradeSets.findByPosition(grade)?.fullName ?? ''

        if (typeof firstLessonOfGrade === 'number') return plotLine(firstLessonOfGrade, label)
        return null
      })
      .filter(isPresent)
  }

  get summaryBoxColorScheme() {
    switch (this.args.precinct) {
      case Precinct.MS_LESSONS:
        return SummaryBoxColorScheme.DarkGreen
      case Precinct.RE_SPELLING:
      case Precinct.REX_SPELLING:
        return SummaryBoxColorScheme.Green
      case Precinct.RE_READING:
      case Precinct.REX_READING:
        return SummaryBoxColorScheme.LightBlue
      default:
        return SummaryBoxColorScheme.LightBlue
    }
  }

  get summaryStats(): Stat[] {
    return [{ label: 'Lessons Completed', value: this.lessonsCompleted.toString() }]
  }

  /**
   * Returns a formatter function to pass to Highcharts to format the tooltip label.
   */
  get toolTipFormatter() {
    const { intl } = this
    const { precinct } = this.args

    return function (this: Point) {
      const { event } = this as Point & { event: { lesson_number: string; score: number } }
      const lessonNumber = event.lesson_number
      const formattedDate = format(new Date(this.x), intl.t('dateFormats.dateFns.dd MMM'))

      // The tooltip for the spelling charts for REX and RE are slighlty different. only spelling has score in response
      if (precinct === Precinct.RE_SPELLING || precinct === Precinct.REX_SPELLING) {
        const { score } = event
        return `Lesson: ${lessonNumber}<br/>Date: ${formattedDate}<br/>Score: ${score}%`
      }
      return `Lesson: ${lessonNumber}</br>Date: ${formattedDate}`
    }
  }

  /**
   * Set the yRange of the chart to include all lessons for all grades in the data
   */
  get yRange(): [number, number] {
    const firstGrade = Math.min(...this.grades)
    const lastGrade = Math.max(...this.grades)

    /* expand grades to lessons range for the chart
     * min: first lesson for first grade
     * max: last lesson for last grade
     */
    const firstLesson = this.lessons.find((l) => l.grade_position === firstGrade)
    const lastLesson = [...this.lessons].reverse().find((l) => l.grade_position === lastGrade)

    // 1 is added to the last lesson, so that the nextGrade plotLine is part of the range
    return [firstLesson?.lesson_position ?? -Infinity, (lastLesson?.lesson_position ?? Infinity) + 1]
  }

  <template>
    <div data-test-giraffe-course-progress-student-details class="h-full" ...attributes>
      <LeftSplitChart>
        <:left>
          <SummaryBox
            class="h-full"
            @title={{@summaryTitle}}
            @stats={{this.summaryStats}}
            @link={{@link}}
            @colorScheme={{this.summaryBoxColorScheme}}
          />
        </:left>
        <:right>
          <ScatterPlot
            @chartMargin={{this.chartMargin}}
            @data={{this.chartData}}
            @fromDate={{@period.start}}
            @legend={{this.legend}}
            @plotLines={{this.plotLines}}
            @toDate={{@period.end}}
            @toolTipFormatter={{this.toolTipFormatter}}
            @yAxisLabel={{this.yAxisLabel}}
            @yRange={{this.yRange}}
          />
        </:right>
      </LeftSplitChart>
    </div>
  </template>
}

export default CourseProgressStudent
